import CircularProgress from "@mui/material/CircularProgress";
import debounce from "lodash-es/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useSubmitCheckout, {
	CHECKOUT_ERROR_TYPES,
	ICheckoutError,
} from "../../../apis/useSubmitCheckout/useSubmitCheckout";
import ArrowRight from "../../../assets/images/ArrowRight.svg";
import { isCartItemLimitExceeded } from "../../../common/helpers/itemOrderLimitHelper";
import { displayCentsWithDollarSign } from "../../../common/helpers/stringUtils";
import { useItemSubtotal } from "../../../common/hooks/useItemSubtotal";
import useOrderTimeRestriction from "../../../common/hooks/useOrderTimeRestriction";
import Condition from "../../../components/Conditional/Condition";
import { Image } from "../../../components/elements";
import PopupDialog from "../../../components/elements/PopupDialog/PopupDialog";
import { useCartContext } from "../../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import {
	usePaymentActions,
	usePaymentContext,
} from "../../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../../router/path";
import { addDatadogAction } from "../../../services/datadog";
import "./SendToKitchenButton.scss";

export const SendToKitchenButton: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const paymentActions = usePaymentActions();
	const { state: paymentData } = usePaymentContext((state) => state.paymentData);
	const {
		state: { cart, is_takeaway, takeaway_byob },
	} = useCartContext((state) => state);
	const { state: order_level_takeaway_fee } = useConfigContext(
		(state) => state.config.takeaway_fee
	);
	const { state: itemLimitPerPax } = useConfigContext((state) => state.config.item_limit_per_pax);
	const { state: pax } = useConfigContext((state) => state.qrSession?.pax);
	const subtotal = useItemSubtotal(cart);
	const takeawayfee = is_takeaway && !takeaway_byob ? order_level_takeaway_fee : 0;

	const { submitCheckout, isLoading, error, removeOosItems, oosItemNames } = useSubmitCheckout();
	const { isExpired, checkExpiry } = useOrderTimeRestriction();

	const [showErrorPopup, setShowErrorPopup] = useState(false);
	const [showItemLimitPopup, setShowItemLimitPopup] = useState(false);
	const [showTimeExpiredPopup, setShowTimeExpiredPopup] = useState(false);
	const [errorAttemptCount, setErrorAttemptCount] = useState(0);

	const { state: table } = useConfigContext((state) => state.table);
	const isDisabled =
		cart.length === 0 || isLoading || isExpired || table?.toLowerCase() === "viewonly";

	const debouncedSubmit = useMemo(
		() =>
			debounce(async () => {
				submitCheckout();
			}, 300),
		[submitCheckout]
	);

	const handleClick = useCallback(() => {
		addDatadogAction("cart.send_to_kitchen_click", { items: cart.length });
		if (checkExpiry()) {
			setShowTimeExpiredPopup(true);

			return;
		}
		if (isCartItemLimitExceeded(cart, itemLimitPerPax, pax ?? 0)) {
			setShowItemLimitPopup(true);

			return;
		}
		setShowErrorPopup(false);
		paymentActions.CLEAR_PAYMENT_DATA();
		debouncedSubmit();
	}, [checkExpiry, cart, itemLimitPerPax, pax, paymentActions, debouncedSubmit]);

	const wasLoadingRef = useRef(false);

	useEffect(() => {
		if (isLoading) {
			wasLoadingRef.current = true;

			return;
		}

		if (!paymentData || !wasLoadingRef.current) return;

		wasLoadingRef.current = false;
		navigate(PATH.SEND_TO_KITCHEN_CONFIRM);
	}, [paymentData, isLoading, navigate]);

	useEffect(() => {
		if (error && !showErrorPopup) {
			setErrorAttemptCount((prev) => prev + 1);
			setShowErrorPopup(true);
		}
	}, [error, showErrorPopup]);

	const isCheckoutError = (err: unknown): err is ICheckoutError =>
		err !== null && typeof err === "object" && "type" in err && "items" in err;

	const isOosError = isCheckoutError(error) && error.type === CHECKOUT_ERROR_TYPES.OOS_ERROR;

	const handleRemoveOosItems = useCallback(() => {
		setShowErrorPopup(false);
		removeOosItems();
	}, [removeOosItems]);

	const handleTryAgain = useCallback(() => {
		setShowErrorPopup(false);
		debouncedSubmit();
	}, [debouncedSubmit]);

	const handleCloseError = useCallback(() => {
		setShowErrorPopup(false);
		setErrorAttemptCount(0);
	}, []);

	const popupButtons = useMemo(() => {
		if (isOosError) {
			return [
				{
					text: t("cartPage.oosRemove"),
					onClick: handleRemoveOosItems,
					className: "PrimaryButton",
				},
			];
		}
		if (errorAttemptCount === 1) {
			return [
				{
					text: t("sendToKitchen.tryAgain"),
					onClick: handleTryAgain,
					className: "PrimaryButton",
				},
			];
		}

		return [
			{
				text: t("sendToKitchen.close"),
				onClick: handleCloseError,
				className: "PrimaryButton",
			},
		];
	}, [isOosError, errorAttemptCount, t, handleTryAgain, handleCloseError, handleRemoveOosItems]);

	const popupContent = useMemo(() => {
		if (isOosError && oosItemNames.length > 0) {
			return (
				<>
					<div className="OosMessage">{t("cartPage.oosMessage")}</div>
					<div className="OosItemsList">
						{oosItemNames.map((name, index) => (
							<div key={index + name} className="OosItem">
								{name}
							</div>
						))}
					</div>
				</>
			);
		}

		return (
			<span>
				{errorAttemptCount === 1
					? t("sendToKitchen.errorTimeoutMessage")
					: t("sendToKitchen.errorFailedMessage")}
			</span>
		);
	}, [isOosError, oosItemNames, errorAttemptCount, t]);

	let errorTitle = t("sendToKitchen.errorFailedTitle");

	if (isOosError) {
		errorTitle = t("cartPage.oosTitle");
	} else if (errorAttemptCount === 1) {
		errorTitle = t("sendToKitchen.errorTimeoutTitle");
	}

	return (
		<>
			<button
				className={`CheckoutButton SendToKitchenButton ${isDisabled ? "Disabled" : ""}`}
				aria-label={t("cartPage.sendToKitchenBtn")}
				onClick={handleClick}
				disabled={isDisabled}
			>
				<div className="CheckoutInfoWrapper">
					<div className="Subtotal">{displayCentsWithDollarSign(subtotal + takeawayfee)}</div>
				</div>
				<div className="CheckoutButtonLabel">
					<Condition check={!isLoading}>
						{t("cartPage.sendToKitchenBtn")}
						<Image src={ArrowRight} alt="ArrowRight" className="ArrowRight" />
					</Condition>
					<Condition check={isLoading}>
						<CircularProgress size={24} />
					</Condition>
				</div>
			</button>

			<PopupDialog
				data-testid="item-limit-exceeded-dialog"
				open={showItemLimitPopup}
				title={t("itemLimit.exceeded.title")}
				content={<span>{t("itemLimit.exceeded.message")}</span>}
				buttons={[
					{ text: t("itemLimit.exceeded.confirm"), onClick: () => setShowItemLimitPopup(false) },
				]}
				onClose={() => setShowItemLimitPopup(false)}
				className="CheckoutErrorPopup"
			/>

			<PopupDialog
				data-testid="order-time-expired-dialog"
				open={showTimeExpiredPopup}
				title={t("orderTimeRestriction.title")}
				content={<span>{t("orderTimeRestriction.message")}</span>}
				buttons={[
					{
						text: t("orderTimeRestriction.ok"),
						onClick: () => setShowTimeExpiredPopup(false),
					},
				]}
				onClose={() => setShowTimeExpiredPopup(false)}
				className="CheckoutErrorPopup"
			/>

			<PopupDialog
				data-testid="send-to-kitchen-error-popup"
				open={showErrorPopup}
				title={errorTitle}
				onClose={handleCloseError}
				content={<div className="ErrorContent">{popupContent}</div>}
				buttons={popupButtons}
				className="CheckoutErrorPopup"
			/>
		</>
	);
};
